import AccountCircle from "@mui/icons-material/AccountCircle";
import HelpIcon from "@mui/icons-material/Help";
import Home from "@mui/icons-material/Home";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import Notifications from "@mui/icons-material/Notifications";
import { Theme, useMediaQuery, useTheme } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import MDBadge from "@/components/atoms/MDBadge";
import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import {
  setMiniSidenav,
  setTransparentNavbar,
  useMaterialUIController,
} from "@/contexts/mui-context";
import { useNotification } from "@/contexts/notification-context";
import usePopOver from "@/hooks/use-popover";
import { SidebarMessages } from "@/messages/layout/sidebar";
import { BreadCrumb } from "@/types/page.types";

import { HelpPopover } from "./components/helpPopover";
import { UserPopover } from "./components/userPopover";
import { ViewMessagesPopover } from "./components/viewMessagesPopover";
import {
  navbar,
  navbarContainer,
  navbarDesktopMenu,
  navbarIconButton,
  navbarMobileMenu,
  navbarRow,
} from "./styles";
import BreadcrumbsNew from "../Breadcrumbs";

interface DashboardNavbarProps {
  absolute?: boolean;
  light?: boolean;
  isMini?: boolean;
  breadCrumbs?: BreadCrumb[];
  title: string;
  messages: SidebarMessages;
  hideBoldTitle?: boolean;
}

function DashboardNavbar({
  absolute = false,
  light = false,
  isMini = false,
  breadCrumbs,
  title,
  messages,
  hideBoldTitle,
}: DashboardNavbarProps) {
  const [navbarType, setNavbarType] = useState<
    "fixed" | "absolute" | "sticky" | "static" | "relative"
  >("static");
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, darkMode } = controller;
  const { unReadConversationsCount, combinedCount } = useNotification();
  const theme = useTheme();
  const isMobileDevice = useMediaQuery(theme.breakpoints.down("lg"));
  const location = useLocation();
  const hideBreadcrumbs = isMobileDevice;

  const {
    openPopover: openHelpPopover,
    anchorEl: helpAnchorEl,
    closePopover: closeHelpPopover,
    isOpen: isHelpOpen,
  } = usePopOver();

  const {
    openPopover: openViewMessagesPopover,
    anchorEl: viewMessagesAnchorEl,
    closePopover: closeViewMessagesPopover,
    isOpen: isViewMessagesOpen,
  } = usePopOver();

  const {
    openPopover: openUserPopover,
    anchorEl: userAnchorEl,
    closePopover: closeUserPopover,
    isOpen: isUserOpen,
  } = usePopOver();

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    window.addEventListener("scroll", handleTransparentNavbar);

    handleTransparentNavbar();

    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);

  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }: Theme) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main!, 0.6) : text.main!;
      }

      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        {!hideBreadcrumbs && (
          <MDBox mb={{ xs: 1, md: 0 }} sx={(thm) => navbarRow(thm, { isMini })}>
            <BreadcrumbsNew
              icon={<Home />}
              breadcrumbs={breadCrumbs}
              title={title}
              light={light}
              hideBoldTitle={hideBoldTitle}
            />
            <IconButton sx={navbarDesktopMenu} onClick={handleMiniSidenav} size="small" disableRipple>
              {miniSidenav ? (
                <MenuOpenIcon fontSize="medium" sx={iconsStyle} />
              ) : (
                <MenuIcon fontSize="medium" sx={iconsStyle} />
              )}
            </IconButton>
          </MDBox>
        )}
        {isMini ? null : (
          <MDBox sx={(theme) => ({ ...navbarRow(theme, { isMini }), ...(hideBreadcrumbs ? { width: '100%', justifyContent: 'space-between', alignItems: 'center', gap: 1 } : {}) })}>
            {hideBreadcrumbs && (
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                onClick={handleMiniSidenav}
                sx={{ flexShrink: 0 }}
              >
                {miniSidenav ? (
                  <MenuOpenIcon sx={iconsStyle} fontSize="medium" />
                ) : (
                  <MenuIcon sx={iconsStyle} fontSize="medium" />
                )}
              </IconButton>
            )}
            {hideBreadcrumbs && (
              <MDBox sx={{ flex: 1, minWidth: 0 }}>
                <BreadcrumbsNew
                  icon={<Home />}
                  breadcrumbs={breadCrumbs}
                  title={title}
                  light={light}
                  compact
                  hideBoldTitle={hideBoldTitle}
                />
              </MDBox>
            )}
            <MDBox color="white" sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, ...(hideBreadcrumbs ? { gap: 0, '& .MuiIconButton-root': { padding: '4px' }, '& .MuiSvgIcon-root': { fontSize: '1.2rem' } } : {}) }}>
              {!hideBreadcrumbs && (
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarMobileMenu}
                  onClick={handleMiniSidenav}
                >
                  {miniSidenav ? (
                    <MenuOpenIcon sx={iconsStyle} fontSize="medium" />
                  ) : (
                    <MenuIcon sx={iconsStyle} fontSize="medium" />
                  )}
                </IconButton>
              )}
              <IconButton
                onClick={(event) => openHelpPopover(event, "")}
                size="medium"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
              >
                <MDBadge color="primary">
                  <HelpIcon sx={iconsStyle} />
                </MDBadge>
              </IconButton>
              <HelpPopover
                isOpen={isHelpOpen}
                anchorEl={helpAnchorEl}
                onClose={closeHelpPopover}
                messages={messages}
              />
              <IconButton
                onClick={(event) => openViewMessagesPopover(event, unReadConversationsCount)}
                size="medium"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
              >
                <MDBadge color="secondary" badgeContent={combinedCount} size="xs">
                  <Notifications sx={iconsStyle} />
                </MDBadge>
              </IconButton>
              <ViewMessagesPopover
                isOpen={isViewMessagesOpen}
                anchorEl={viewMessagesAnchorEl}
                onClose={closeViewMessagesPopover}
                messages={messages}
              />
              <IconButton
                onClick={(event) => openUserPopover(event, "")}
                size="medium"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
              >
                <MDBadge color="primary">
                  <AccountCircle sx={iconsStyle} />
                </MDBadge>
              </IconButton>
              <UserPopover
                isOpen={isUserOpen}
                anchorEl={userAnchorEl}
                onClose={closeUserPopover}
                messages={messages}
              />
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

export default DashboardNavbar;
